import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Col,
  Form,
  Row,
  Space,
  TreeSelect as AntTreeSelect,
  Typography,
} from 'antd'
import { includes, isEmpty, isNil, map, size } from 'lodash'
import PropTypes from 'prop-types'

import { SIZES } from '../../constants'
import { handleTreeSelectDropdownSearchHighlights } from '../../helpers'
import { antFormItemNamePropType } from '../../prop-types'
import { formatFormItemLabel, formatFormListLabel } from '../../utils'

import ListItemTreeSelect from './list-item-tree-select'

const FormListTreeSelect = ({
  treeData,
  name,
  label,
  showCountItems,
  newItemTreeSelectPlaceholder,
  form,
  rules,
  itemRules,
  isDirty,
  showColon,
  labelAbove,
  itemRender,
  ...rest
}) => {
  const [listSize, setListSize] = useState(size(form.getFieldValue(name)))

  const labelContent = useMemo(() => {
    const formListLabel = formatFormListLabel(label, listSize, showCountItems)

    return formatFormItemLabel(formListLabel, showColon)
  }, [listSize, label, showColon, showCountItems])

  const shouldModify = useCallback(
    value => {
      const existingValues = form.getFieldValue(name)
      return !includes(existingValues, value)
    },
    [form, name],
  )

  const handleAddItemWithValue = useCallback(
    add => value => {
      if (shouldModify(value)) {
        add(value)
        setListSize(previousSize => previousSize + 1)
      }
    },
    [shouldModify],
  )
  const handleRemoveItemByName = useCallback(
    remove => name => {
      remove(name)
      setListSize(previousSize => previousSize - 1)
    },
    [],
  )

  const listItemRender = useCallback(
    ({ key, name, fieldKey, ...restField }, remove) => {
      if (!isNil(itemRender)) {
        return itemRender({
          field: { key, name, fieldKey, ...restField },
          shouldModify,
          treeData,
          onClear: handleRemoveItemByName(remove),
          itemRules,
          ...rest,
        })
      }

      return (
        <Form.Item
          key={key}
          name={name}
          fieldKey={fieldKey}
          rules={itemRules}
          noStyle={isEmpty(itemRules)}
          {...restField}
        >
          <ListItemTreeSelect
            shouldModify={shouldModify}
            name={name}
            treeData={treeData}
            onClear={handleRemoveItemByName(remove)}
            {...rest}
          />
        </Form.Item>
      )
    },
    [
      itemRender,
      itemRules,
      shouldModify,
      treeData,
      handleRemoveItemByName,
      rest,
    ],
  )

  useEffect(() => {
    setListSize(size(form.getFieldValue(name)))
  }, [form, isDirty, name])

  return (
    <Row align="middle">
      {labelAbove ? (
        <Col className="ant-form-item-label" span={24}>
          <Typography.Paragraph style={{ height: '32px', margin: 0 }}>
            {labelContent}
          </Typography.Paragraph>
        </Col>
      ) : (
        <Col span={3}>
          <Typography.Text>{labelContent}</Typography.Text>
        </Col>
      )}
      <Col span={labelAbove ? 24 : 21}>
        <Form.List rules={rules} label={label} name={name}>
          {(fields, { add, remove }, { errors }) => (
            <>
              <Space wrap>
                {map(fields, field => listItemRender(field, remove))}
                <Form.Item noStyle={isEmpty(itemRules)}>
                  <AntTreeSelect
                    onChange={handleAddItemWithValue(add)}
                    placeholder={newItemTreeSelectPlaceholder}
                    treeData={treeData}
                    value={null}
                    dropdownMatchSelectWidth={SIZES.DROPDOWN_MIN_WIDTH}
                    dropdownRender={handleTreeSelectDropdownSearchHighlights}
                    treeNodeFilterProp="title"
                    {...rest}
                  />
                </Form.Item>
              </Space>
              <Row>
                <Form.ErrorList errors={errors} />
              </Row>
            </>
          )}
        </Form.List>
      </Col>
    </Row>
  )
}

FormListTreeSelect.propTypes = {
  name: antFormItemNamePropType.isRequired,
  label: PropTypes.string,
  showCountItems: PropTypes.bool,
  newItemTreeSelectPlaceholder: PropTypes.string,
  treeData: PropTypes.array.isRequired,
  rules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  itemRules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  form: PropTypes.object.isRequired,
  isDirty: PropTypes.bool,
  showColon: PropTypes.bool,
  labelAbove: PropTypes.bool,
  itemRender: PropTypes.func,
}

FormListTreeSelect.defaultProps = {
  label: '',
  showCountItems: false,
  newItemTreeSelectPlaceholder: 'Select a new item...',
  rules: undefined,
  itemRules: undefined,
  isDirty: false,
  showColon: false,
  labelAbove: true,
  itemRender: undefined,
}

export default FormListTreeSelect
